/**
 * The client.
 *
 * `AppRouter` is imported from the server as a **type only**, which is the whole
 * point of tRPC here ([D-005](../../../docs/DECISIONS.md#d-005)): the client and
 * the server cannot disagree about a field name, and there is no generated
 * schema to keep in step. The import erases at compile time, so nothing on the
 * server — mysql2, argon2 — can reach this bundle.
 *
 * There is no state library. A session is a token and a learner id; a lesson is
 * fetched once and answered locally ([D-050](../../../docs/DECISIONS.md#d-050)).
 * Anything more would be machinery for state this app does not have.
 */

import type { AppRouter } from '@capytalk/server';
import type { TRPCClient } from '@trpc/client';
import { TRPCClientError, createTRPCClient, httpLink } from '@trpc/client';

/**
 * Where the API lives.
 *
 * Same origin in production — the app and the server are one deploy on one
 * Linode ([Operations](../../../docs/OPERATIONS.md)) — and the Vite dev server
 * proxies `/trpc` to `localhost:8787` in development, so this is a bare path in
 * both cases and never a configured host.
 */
export const API_PATH = '/trpc';

/** The session, as this app holds it. */
export interface Session {
  readonly token: string;
  readonly learnerId: string;
  readonly deviceId: string;
}

/**
 * Where the session lives.
 *
 * `sessionStorage`, not `localStorage`: a token is the whole authorisation for
 * fifteen minutes, and a shared family iPad should not carry one child's session
 * into the next child's tab. Closing the tab signs out, which is the right
 * default for a device several people use.
 */
const SESSION_KEY = 'capytalk.session';

export function readSession(): Session | null {
  try {
    const raw = globalThis.sessionStorage.getItem(SESSION_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as Session).token !== 'string'
    ) {
      return null;
    }
    return parsed as Session;
  } catch {
    // Private browsing, cleared storage, or something that is not ours. A
    // missing session is a sign-in screen, never an error.
    return null;
  }
}

export function writeSession(session: Session | null): void {
  try {
    if (session === null) globalThis.sessionStorage.removeItem(SESSION_KEY);
    else globalThis.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Storage refused. The session still works for this page; the child signs
    // in again after a reload. Better than refusing to sign in at all.
  }
}

/**
 * Annotated rather than inferred: the inferred type reaches into the server's
 * `Context`, which is a server-only shape and has no business being nameable
 * from a browser bundle.
 */
export type Client = TRPCClient<AppRouter>;

/**
 * A lesson as the **wire** delivers it, not as the server builds it.
 *
 * `lesson.start` deliberately strips the builder's diagnostics before answering
 * — what it left out is authoring feedback, not lesson content
 * ([D-056](../../../docs/DECISIONS.md#d-056)) — so `StartedLesson` is the wrong
 * type here by exactly that field. Deriving from the client keeps this honest:
 * if the router stops sending something, this stops having it.
 */
export type Started = Awaited<ReturnType<Client['lesson']['start']['mutate']>>;

/**
 * A practice session, which is a lesson-shaped thing with no lesson.
 *
 * The runner plays both, so the types it holds are the union: it reads only
 * what the two have in common, and TypeScript is what keeps that honest
 * ([D-074](../../../docs/DECISIONS.md#d-074)).
 */
export type StartedPractice = Awaited<
  ReturnType<Client['practice']['start']['mutate']>
>;

/** Either kind, as the runner receives it. */
export type Playing = Started | StartedPractice;

/** One exercise, as the wire delivers it. */
export type Queued = Started['exercises'][number];

/** The path, as the wire delivers it. */
export type Path = Awaited<ReturnType<Client['path']['chapter']['query']>>;

/** Today's three quests, and the day they are counted in. */
export type QuestDay = Awaited<ReturnType<Client['quest']['today']['query']>>;

/** Every word this child has, for the collection screen. */
export type Collection = Awaited<ReturnType<Client['learner']['collection']['query']>>;

/** What a finished lesson earned, and where the streak stands. */
export type Finished = Awaited<ReturnType<Client['lesson']['finish']['mutate']>>;

export type FinishedPractice = Awaited<
  ReturnType<Client['practice']['finish']['mutate']>
>;

/**
 * Either kind's closing answer.
 *
 * Both carry a `payout`, today's `quests` and a `recap` — the runner plays both
 * and the recap screen reads only what the two have in common. `recap.next` is
 * the one field that differs in meaning rather than in shape: a practice session
 * has no place on the path, so it is always null there.
 */
export type Finishing = Finished | FinishedPractice;

/** XP, acorns and the streak, summed from the ledgers on every read. */
export type Wallet = Awaited<ReturnType<Client['learner']['wallet']['query']>>;

/**
 * Today, as the child's own device reckons it.
 *
 * Streaks are counted in local days ([D-021](../../../docs/DECISIONS.md#d-021)),
 * and only this device knows which timezone it is in. Built from the local
 * parts rather than from `toISOString`, which would give the UTC day — the
 * wrong one for a Danish child playing after 11pm in summer.
 */
export function today(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Everyone who wants to know that something on the server just changed.
 *
 * The acorns in the top bar are read once and shown everywhere, but the shop,
 * a chest, a dye or a title can spend or add to them from anywhere below it.
 * Telling each of those screens to tell the top bar is how one of them gets
 * forgotten, and a child who has just bought a hat sees the old number up top
 * and the new one in the shop. So the client says it once, for every change:
 * any mutation that succeeded may have moved a balance, and whoever shows one
 * reads it again.
 */
const listeners = new Set<() => void>();

/** Hear about every change the server accepted. Returns the way to stop. */
export function onChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Tell everyone listening that the server has accepted a change. */
export function changed(): void {
  for (const listener of [...listeners]) listener();
}

export function createClient(token: () => string | null): Client {
  return createTRPCClient<AppRouter>({
    links: [
      httpLink({
        url: API_PATH,
        // A tRPC mutation is a POST and a query a GET, so a POST that was
        // answered with a 2xx is a change the server kept.
        fetch: async (input, init) => {
          const response = await fetch(input, init as RequestInit | undefined);
          if (init?.method === 'POST' && response.ok) changed();
          return response;
        },
        headers: () => {
          const current = token();
          return current === null ? {} : { authorization: `Bearer ${current}` };
        },
      }),
    ],
  });
}

/**
 * What went wrong, in words a screen can show.
 *
 * The server answers every bad credential identically on purpose — an unknown
 * device, a revoked one and a wrong secret are one message
 * ([Security](../../../docs/SECURITY.md)) — so this does not try to be more
 * specific than the server was. It only separates *what the child should do*:
 * try again, wait, or tell a grown-up.
 */
export type Trouble =
  /**
   * The server refused. At sign-in that means a wrong credential; while signed
   * in it means the token has expired, because they are the same `UNAUTHORIZED`
   * and the server is right not to distinguish them. The caller knows which
   * situation it is in; this deliberately does not guess.
   */
  | { readonly kind: 'rejected' }
  | { readonly kind: 'locked'; readonly message: string }
  | { readonly kind: 'missing' }
  | { readonly kind: 'offline' }
  | { readonly kind: 'unknown' };

export function troubleFrom(error: unknown): Trouble {
  // Not a tRPC error at all means the request never got an answer: no network,
  // server down, DNS. A child sees "check the wifi", not a stack trace.
  if (!(error instanceof TRPCClientError)) return { kind: 'offline' };

  const code: unknown = (error.data as { code?: unknown } | null)?.code;
  switch (code) {
    case 'UNAUTHORIZED':
      return { kind: 'rejected' };
    case 'TOO_MANY_REQUESTS':
      return { kind: 'locked', message: error.message };
    case 'NOT_FOUND':
      return { kind: 'missing' };
    default:
      return { kind: 'unknown' };
  }
}
